import {
  Award,
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Pencil,
  Plus,
  Search,
  Table as TableIcon,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useCourses } from '@/features/lms/courses/queries';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useSession } from '@/stores/session';
import { BASE as ASSESSMENTS_BASE } from '../api';
import { AssessmentFormDialog } from '../components/AssessmentFormDialog';
import { RecordAttemptDialog } from '../components/RecordAttemptDialog';
import {
  useAssessments,
  useAttempts,
  useCreateAssessment,
  useDeleteAssessment,
  useUpdateAssessment,
} from '../queries';
import { useAssessmentsUi, type AssessmentsViewMode } from '../store/viewPrefs';
import type { Assessment, AssessmentInput } from '../types';

const HR_ROLES = new Set(['founder', 'super_admin', 'hr']);

/** Columns the backend lets us order by — keeps typos out of the URL. */
type AssessmentOrderingColumn =
  | 'title'
  | 'max_score'
  | 'passing_score'
  | 'is_active';

const VIEW_MODES: readonly ViewModeOption<AssessmentsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by active', icon: Layers },
];

const ACTIVE_GROUPS = [
  { key: 'active', label: 'Active', value: true },
  { key: 'inactive', label: 'Inactive', value: false },
] as const;

export function AssessmentsListPage() {
  const role = useSession((s) => s.role);
  const canManage = role ? HR_ROLES.has(role) : false;

  const view = useAssessmentsUi((s) => s.view);
  const setView = useAssessmentsUi((s) => s.setView);
  const ordering = useAssessmentsUi((s) => s.ordering);
  const setOrdering = useAssessmentsUi((s) => s.setOrdering);

  // Server-driven filters live in local state — pagination + search hit the API.
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [courseFilter, setCourseFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [page, setPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Assessment | undefined>(undefined);
  const [recordingFor, setRecordingFor] = useState<Assessment | null>(null);
  const [toDelete, setToDelete] = useState<Assessment | null>(null);

  const courseParam = courseFilter === 'all' ? undefined : Number(courseFilter);
  const isActiveParam = activeFilter === 'all' ? undefined : activeFilter === 'active';

  const { data, isPending, isError, error, isFetching } = useAssessments({
    page,
    search: search || undefined,
    course: courseParam,
    is_active: isActiveParam,
    ordering,
  });
  const { data: coursesData } = useCourses({});
  const { data: attemptsData } = useAttempts();

  const courseTitles = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of coursesData?.results ?? []) map.set(c.id, c.title);
    return map;
  }, [coursesData]);
  const courseTitle = (id: number) => courseTitles.get(id) ?? `#${id}`;

  const create = useCreateAssessment();
  const update = useUpdateAssessment();
  const remove = useDeleteAssessment();

  const assessments = data?.results ?? [];

  async function handleSubmit(input: AssessmentInput) {
    if (editing) await update.mutateAsync({ id: editing.id, input });
    else await create.mutateAsync(input);
  }

  const startCreate = () => {
    setEditing(undefined);
    setFormOpen(true);
  };
  const startEdit = (a: Assessment) => {
    setEditing(a);
    setFormOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Assessments</h1>
          <p className="text-muted-foreground text-sm">
            Record scores against a course assessment. Pass/fail is decided server-side.
          </p>
        </div>
        {canManage ? (
          <Button onClick={startCreate}>
            <Plus className="size-4" /> New assessment
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by title…"
              className="pl-8"
              aria-label="Search assessments"
            />
          </div>
          <Select
            value={courseFilter}
            onValueChange={(v) => {
              setPage(1);
              setCourseFilter(v);
            }}
          >
            <SelectTrigger className="w-48" aria-label="Filter by course">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {(coursesData?.results ?? []).map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={activeFilter}
            onValueChange={(v) => {
              setPage(1);
              setActiveFilter(v);
            }}
          >
            <SelectTrigger className="w-40" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `assessments.csv` without leaving the SPA.
              void exportCsv(ASSESSMENTS_BASE, {
                search: search || undefined,
                course: courseParam,
                is_active: isActiveParam,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export assessments as CSV"
            title="Export assessments as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load assessments.'}
        </p>
      ) : isPending ? (
        <AssessmentsSkeleton />
      ) : assessments.length === 0 ? (
        <AssessmentsTable
          assessments={[]}
          courseTitle={courseTitle}
          canManage={canManage}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onEdit={startEdit}
          onDelete={setToDelete}
          onRecord={setRecordingFor}
        />
      ) : view === 'table' ? (
        <AssessmentsTable
          assessments={assessments}
          courseTitle={courseTitle}
          canManage={canManage}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onEdit={startEdit}
          onDelete={setToDelete}
          onRecord={setRecordingFor}
        />
      ) : view === 'list' ? (
        <AssessmentsList
          assessments={assessments}
          courseTitle={courseTitle}
          onRecord={setRecordingFor}
        />
      ) : view === 'cards' ? (
        <AssessmentsCards
          assessments={assessments}
          courseTitle={courseTitle}
          canManage={canManage}
          onEdit={startEdit}
          onDelete={setToDelete}
          onRecord={setRecordingFor}
        />
      ) : (
        <AssessmentsGroupedByActive
          assessments={assessments}
          courseTitle={courseTitle}
          canManage={canManage}
          onEdit={startEdit}
          onDelete={setToDelete}
          onRecord={setRecordingFor}
        />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'assessment' : 'assessments'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent attempts</CardTitle>
        </CardHeader>
        <CardContent>
          {attemptsData && attemptsData.results.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assessment</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attemptsData.results.map((row) => {
                  const a = assessments.find((x) => x.id === row.assessment);
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{a?.title ?? `#${row.assessment}`}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.score}
                        {a ? `/${a.max_score}` : ''}
                      </TableCell>
                      <TableCell>
                        {row.passed ? (
                          <Badge variant="secondary">Passed</Badge>
                        ) : (
                          <Badge variant="destructive">Did not pass</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {new Date(row.attempted_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-muted-foreground flex min-h-48 items-center justify-center py-8 text-center text-sm">
              No attempts recorded yet.
            </div>
          )}
        </CardContent>
      </Card>

      <AssessmentFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        assessment={editing}
        onSubmit={handleSubmit}
      />

      <RecordAttemptDialog
        open={Boolean(recordingFor)}
        onOpenChange={(o) => !o && setRecordingFor(null)}
        assessment={recordingFor}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              “{toDelete?.title}” will be removed. Recorded attempts will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ViewProps {
  assessments: Assessment[];
  courseTitle: (id: number) => string;
  canManage: boolean;
  onEdit: (a: Assessment) => void;
  onDelete: (a: Assessment) => void;
  onRecord: (a: Assessment) => void;
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <Badge variant={active ? 'secondary' : 'outline'}>{active ? 'Active' : 'Inactive'}</Badge>
  );
}

function ManageActions({
  assessment,
  canManage,
  onEdit,
  onDelete,
  onRecord,
}: {
  assessment: Assessment;
  canManage: boolean;
  onEdit: (a: Assessment) => void;
  onDelete: (a: Assessment) => void;
  onRecord: (a: Assessment) => void;
}) {
  return (
    <div className="flex justify-end gap-1">
      {assessment.is_active ? (
        <Button size="sm" variant="outline" onClick={() => onRecord(assessment)}>
          <Award className="size-4" /> Record attempt
        </Button>
      ) : null}
      {canManage ? (
        <>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Edit assessment"
            onClick={() => onEdit(assessment)}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Delete assessment"
            onClick={() => onDelete(assessment)}
          >
            <Trash2 className="size-4" />
          </Button>
        </>
      ) : null}
    </div>
  );
}

interface AssessmentsTableProps extends ViewProps {
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function AssessmentsTable({
  assessments,
  courseTitle,
  canManage,
  ordering,
  onOrderingChange,
  onEdit,
  onDelete,
  onRecord,
}: AssessmentsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<AssessmentOrderingColumn>
              column="title"
              value={ordering}
              onChange={onOrderingChange}
            >
              Title
            </SortableTableHead>
            <TableHead className="hidden sm:table-cell">Course</TableHead>
            <SortableTableHead<AssessmentOrderingColumn>
              column="max_score"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Max score
            </SortableTableHead>
            <SortableTableHead<AssessmentOrderingColumn>
              column="passing_score"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Passing score
            </SortableTableHead>
            <SortableTableHead<AssessmentOrderingColumn>
              column="is_active"
              value={ordering}
              onChange={onOrderingChange}
            >
              Active
            </SortableTableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {assessments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-muted-foreground py-10 text-center">
                No assessments found.
              </TableCell>
            </TableRow>
          ) : (
            assessments.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium">{a.title}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {courseTitle(a.course)}
                </TableCell>
                <TableCell className="hidden tabular-nums md:table-cell">{a.max_score}</TableCell>
                <TableCell className="hidden tabular-nums md:table-cell">
                  {a.passing_score}
                </TableCell>
                <TableCell>
                  <ActiveBadge active={a.is_active} />
                </TableCell>
                <TableCell>
                  <ManageActions
                    assessment={a}
                    canManage={canManage}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onRecord={onRecord}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function AssessmentsList({
  assessments,
  courseTitle,
  onRecord,
}: {
  assessments: Assessment[];
  courseTitle: (id: number) => string;
  onRecord: (a: Assessment) => void;
}) {
  return (
    <ul className="divide-y rounded-md border">
      {assessments.map((a) => (
        <li
          key={a.id}
          className="flex items-center gap-3 px-3 py-2 text-left"
        >
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">{a.title}</span>
          <span className="text-muted-foreground hidden truncate text-xs sm:inline">
            {courseTitle(a.course)}
          </span>
          <span className="text-muted-foreground hidden text-xs tabular-nums md:inline">
            {a.passing_score}/{a.max_score}
          </span>
          <ActiveBadge active={a.is_active} />
          {a.is_active ? (
            <Button size="sm" variant="outline" onClick={() => onRecord(a)}>
              <Award className="size-4" /> Record
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function AssessmentsCards({
  assessments,
  courseTitle,
  canManage,
  onEdit,
  onDelete,
  onRecord,
}: ViewProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {assessments.map((a) => (
        <Card key={a.id}>
          <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
            <div>
              <CardTitle className="text-base">{a.title}</CardTitle>
              <CardDescription>
                {courseTitle(a.course)} · passing{' '}
                <span className="tabular-nums">
                  {a.passing_score}/{a.max_score}
                </span>
              </CardDescription>
            </div>
            <ActiveBadge active={a.is_active} />
          </CardHeader>
          <CardContent className="space-y-3">
            {a.description ? (
              <p className="text-muted-foreground line-clamp-2 text-sm">{a.description}</p>
            ) : null}
            <ManageActions
              assessment={a}
              canManage={canManage}
              onEdit={onEdit}
              onDelete={onDelete}
              onRecord={onRecord}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AssessmentsGroupedByActive({
  assessments,
  courseTitle,
  canManage,
  onEdit,
  onDelete,
  onRecord,
}: ViewProps) {
  // Bucket by active flag; skip empty groups.
  const groups = useMemo(
    () =>
      ACTIVE_GROUPS.map((g) => ({
        ...g,
        bucket: assessments.filter((a) => a.is_active === g.value),
      })),
    [assessments],
  );

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`assessments-active-${key}`}>
            <h2
              id={`assessments-active-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <AssessmentsCards
              assessments={bucket}
              courseTitle={courseTitle}
              canManage={canManage}
              onEdit={onEdit}
              onDelete={onDelete}
              onRecord={onRecord}
            />
          </section>
        );
      })}
    </div>
  );
}

function AssessmentsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
